import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/shared/ThemeToggle';
import NavigationMenu from '@/components/shared/Menu';
import { getApiUrl } from '@/config/api';
import { CheckCircle2 } from '@/utils/icons';

const ConnectHawkin: React.FC = () => {
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [apiKey, setApiKey] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);

    useEffect(() => {
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                setUserInfo(JSON.parse(userInfoString));
            } catch {
                // ignore
            }
        }
    }, [navigate]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!apiKey.trim()) {
            setError('Please enter your Hawkin API key.');
            return;
        }
        const access = localStorage.getItem('access');
        if (!access) {
            navigate('/login');
            return;
        }
        setLoading(true);
        setError('');
        setSuccess('');
        try {
            const response = await fetch(getApiUrl('/api/hawkin/connect/'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${access}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ api_key: apiKey.trim() }),
            });
            const data = await response.json().catch(() => ({}));
            if (response.ok) {
                setSuccess(
                    `Hawkin connected! ${data.athletes || 0} athletes found. Your test data will sync shortly.`
                );
                setApiKey('');
            } else {
                setError(data.error || data.details || `Request failed (${response.status})`);
            }
        } catch (err: any) {
            setError(err.message || 'Failed to connect. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
            <div className="fixed top-4 left-4 z-50">
                <NavigationMenu
                    userInfo={userInfo}
                    isDark={isDark}
                    showNavigation={true}
                />
            </div>
            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-md w-full space-y-6">
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Connect Hawkin Dynamics
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Enter your Hawkin API key to link your force plate data.
                    </p>
                </div>

                {error && (
                    <div className="cgm-alert-error px-4 py-3 text-sm">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="cgm-alert-success px-4 py-3 text-sm flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 shrink-0" />
                        {success}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="cgm-card p-6 space-y-4">
                    <div>
                        <label htmlFor="hawkin-api-key" className="block cgm-text-primary text-sm font-medium mb-1">
                            Hawkin API Key
                        </label>
                        <input
                            id="hawkin-api-key"
                            type="password"
                            value={apiKey}
                            onChange={(e) => setApiKey(e.target.value)}
                            placeholder="Paste your Hawkin API key"
                            className="cgm-input w-full"
                            disabled={loading}
                            autoComplete="off"
                        />
                        <p className="cgm-text-secondary text-xs mt-1">
                            Get an API key from cloud.hawkindynamics.com → Integrations
                        </p>
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        className={`cgm-button w-full px-6 py-3 font-semibold transition-all duration-200 ${
                            loading ? 'opacity-50 cursor-not-allowed' : ''
                        }`}
                    >
                        {loading ? 'Connecting...' : 'Connect Hawkin'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default ConnectHawkin;
